import { z } from 'zod';

export const ChainLifecycleState = z.enum([
  'captured',
  'interpreting',
  'needs_input',
  'proposed',
  'ready',
  'active',
  'waiting',
  'outcome_review',
  'closed',
  'cancelled',
]);
export const ChainRiskClass = z.enum(['low', 'moderate', 'high', 'prohibited']);
export const ChainActionKind = z.enum([
  'worker_action',
  'owner_decision',
  'system_action',
  'external_action',
  'verification',
  'communication',
]);
export const ChainActionInput = z.object({
  stable_key: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  kind: ChainActionKind,
  title: z.string().min(1).max(500),
  responsible_party: z.string().min(1).max(80).optional(),
  risk_class: ChainRiskClass.default('low'),
});
export const ChainDependencyInput = z
  .object({
    predecessor_key: z.string().min(1).max(80),
    successor_key: z.string().min(1).max(80),
    kind: z.enum(['completion', 'decision', 'evidence', 'time', 'external']),
  })
  .refine((v) => v.predecessor_key !== v.successor_key, 'A chain action cannot depend on itself.');
export const CreateFixtureChainRequest = z.object({
  original_intent: z.string().min(1).max(2000),
  source_language: z.string().min(2).max(16),
  intended_outcome: z.string().min(1).max(2000),
  acceptance_criteria: z.array(z.string().min(1).max(500)).max(20).default([]),
  chain_family: z.enum(['pool_filter', 'restaurant_reservation', 'guest_preparation']),
  risk_class: ChainRiskClass.default('low'),
  actions: z.array(ChainActionInput).min(1).max(50),
  dependencies: z.array(ChainDependencyInput).max(100).default([]),
  waits: z
    .array(
      z.object({
        action_key: z.string().max(80).optional(),
        awaited_kind: z.string().min(1).max(80),
        awaited_from: z.string().min(1).max(80),
        expected_resolution_at: z.string().datetime().optional(),
      }),
    )
    .max(20)
    .default([]),
  obligations: z
    .array(
      z.object({
        source_action_key: z.string().max(80).optional(),
        title: z.string().min(1).max(500),
        owner_kind: z.string().min(1).max(80),
        due_at: z.string().datetime().optional(),
        resolution_policy: z.string().min(1).max(500),
      }),
    )
    .max(20)
    .default([]),
  idempotency_key: z.string().min(8).max(64),
});
export type CreateFixtureChain = z.infer<typeof CreateFixtureChainRequest>;

export const ReviseChainRequest = z.object({
  expected_revision: z.number().int().positive(),
  source: z.enum([
    'fixture',
    'owner_correction',
    'worker_blocker',
    'new_context',
    'external_event',
    'system_policy',
    'ai_reproposal',
  ]),
  reason_code: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  intended_outcome: z.string().min(1).max(2000),
});
export type ReviseChain = z.infer<typeof ReviseChainRequest>;

const PlannerTruthLabel = z.enum([
  'confirmed_fact',
  'observed_pattern',
  'general_practice',
  'inference',
  'missing_information',
]);
const PlannerEvidenceRef = z.object({
  id: z.string().min(1).max(80),
  truth_label: PlannerTruthLabel,
});
export const ChainPlannerInput = z.object({
  original_intent: z.string().min(3).max(1000),
  source_language: z.string().min(2).max(16),
  chain_family: z.enum(['pool_filter', 'restaurant_reservation', 'guest_preparation']),
  correlation_id: z.string().min(8).max(64),
  context_snapshot_id: z.string().min(1).max(40).optional(),
  evidence: z
    .array(
      z.object({
        id: z.string().min(1).max(80),
        summary: z.string().min(1).max(500),
        truth_label: PlannerTruthLabel,
      }),
    )
    .max(40)
    .default([]),
});
export const ChainPlannerProposal = z
  .object({
    intended_outcome: z.string().min(1).max(1000),
    acceptance_criteria: z.array(z.string().min(1).max(300)).min(1).max(12),
    actions: z
      .array(
        z.object({
          stable_key: z
            .string()
            .min(1)
            .max(80)
            .regex(/^[a-z0-9_]+$/),
          kind: ChainActionKind,
          title: z.string().min(1).max(300),
          risk_class: ChainRiskClass,
          evidence_refs: z.array(PlannerEvidenceRef).max(12),
          requires_confirmation: z.boolean(),
        }),
      )
      .min(1)
      .max(30),
    dependencies: z.array(ChainDependencyInput).max(60),
    gaps: z
      .array(
        z.object({
          code: z.string().min(1).max(80),
          question: z.string().min(1).max(300),
          risk_class: ChainRiskClass,
          blocking: z.boolean(),
        }),
      )
      .max(12),
    assumptions: z
      .array(
        z.object({
          statement: z.string().min(1).max(300),
          truth_label: PlannerTruthLabel,
          evidence_refs: z.array(PlannerEvidenceRef).max(8),
        }),
      )
      .max(12),
    confidence: z.number().min(0).max(1),
  })
  .strict();
export type ChainPlannerInputType = z.infer<typeof ChainPlannerInput>;
export type ChainPlannerProposalType = z.infer<typeof ChainPlannerProposal>;
export const ChainPlannerCorrectionInput = z.object({
  correction_kind: z.enum([
    'outcome',
    'action_add',
    'action_remove',
    'action_change',
    'dependency',
    'risk',
    'assumption',
  ]),
  material: z.boolean(),
  field_path: z.string().min(1).max(200),
  reason_code: z
    .string()
    .min(2)
    .max(80)
    .regex(/^[a-z0-9_]+$/),
  replacement_value: z.unknown().optional(),
});
